import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, Wrench, AlertTriangle, ClipboardList, Box, Search, Camera,
  Truck, Users, ClipboardCheck, Check, CalendarClock, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { MaintenanceProSheet } from '@/components/maintenance/MaintenanceProSheet';
import { MaintenanceQuickLogSheet } from '@/components/maintenance/MaintenanceQuickLogSheet';
import { VehicleHistorySheet } from '@/components/maintenance/VehicleHistorySheet';
import { JobStepper } from '@/components/maintenance/JobStepper';
import { SparePartsPanel } from '@/pages/maintenance/SparePartsPanel';
import { ServicePlanPanel } from '@/pages/maintenance/ServicePlanPanel';
import { useAuth } from '@/lib/auth';
import { InspectionsPanel } from '@/pages/inspections/InspectionsPage';
import { VehiclesPanel } from '@/pages/maintenance/VehiclesPanel';

/**
 * Maintenance, arranged as the work actually runs.
 *
 * The page used to show a ledger: every job in one list, sorted by date, with
 * four buckets — all, open, paid, cancelled. Underneath it the server has
 * always modelled a repair as eight stages, each with a control between it and
 * the next. Flattening eight stages into four buckets did not simplify the
 * process; it hid it. Of the first hundred and two jobs, ninety-six went
 * straight to paid: no diagnosis written, no odometer taken, no photograph, no
 * second pair of eyes. Not one job has ever recorded a work-started time.
 *
 * So the pipeline is the page now. The strip across the top is the process,
 * with the count of jobs standing at each stage; pressing one filters to it.
 * Every row says what its next step is, and opening a job opens the ladder
 * rather than a summary.
 *
 * The other change is Service plan. Everything else here answers "what broke".
 * A fleet is looked after by the tab that answers "what is nearly due", and
 * that table was empty for all seventy-one trucks.
 */

type Tab = 'events' | 'upcoming' | 'plan' | 'inspections' | 'vehicles' | 'parts' | 'cost';
type SortBy = 'date_desc' | 'date_asc' | 'cost_desc' | 'vehicle' | 'driver';

interface MaintenanceEvent {
  id: string;
  vehicle_id?: string | null;
  vehicle_plate: string | null;
  event_type: string;
  status: string;
  priority?: string | null;
  reported_at: string | null;
  service_location?: string | null;
  parts_cost_sar?: string | null;
  labor_cost_sar?: string | null;
  total_cost_sar?: string | null;
  description: string | null;
  // PR-NEOM-2 — resolved driver (reporter, else truck's current driver).
  driver_name?: string | null;
  driver_id?: string | null;
  photo_count?: number;
  invoice_count?: number;
  is_repeat_issue?: boolean;
  prior_event_id?: string | null;
  days_since_prior?: number | null;
}

interface DashSummary {
  open_events?: number;
  pending_verification?: number;
  pending_approval?: number;
  pending_payment?: number;
  month_spend_sar?: string;
  low_stock_count?: number;
  service_due_today?: number;
  repeat_this_week?: number;
  // PR-NEOM-2 — active trucks whose last oil change is 75+ days old.
  oil_due_soon?: number;
}

interface ByDriverRow {
  driver_id: string;
  driver_name: string | null;
  event_count: number;
  total_cost_sar: string | null;
}

interface UpcomingRow {
  id: string;
  vehicle_id: string;
  vehicle_plate: string | null;
  event_type: string;
  /** Older shape, kept so a cached response still renders. */
  next_service_due_date?: string | null;
  next_due_date: string | null;
  next_due_km: number | null;
  current_odometer_km: number | null;
  km_remaining: number | null;
  days_remaining: number | null;
}

interface PartRow {
  id: string;
  part_code: string;
  part_name: string;
  category: string | null;
  current_stock: number;
  min_stock: number;
  unit_cost_sar: string | null;
  vendor: string | null;
  is_low: boolean;
}

interface CostRow {
  vehicle_code: string | null;
  plate_en: string | null;
  current_odometer_km: string | null;
  maintenance_cost_sar: string;
  sar_per_km: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSar0 = (n: number) => 'SAR ' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });

const TYPE_LABEL: Record<string, string> = {
  oil_change: 'Oil change', oil_filter: 'Oil filter', air_filter: 'Air filter',
  fuel_filter: 'Fuel filter', cabin_filter: 'Cabin filter',
  brake_pads_front: 'Brake pads (front)', brake_pads_rear: 'Brake pads (rear)',
  brake_discs: 'Brake discs', brake_fluid: 'Brake fluid',
  tire_replacement: 'Tire change', tire_rotation: 'Tire rotation', tire_puncture: 'Tire puncture',
  tire_wear: 'Tire wear',
  battery: 'Battery', alternator: 'Alternator', starter: 'Starter',
  fuel_pump: 'Fuel pump', water_pump: 'Water pump',
  coolant_flush: 'Coolant flush', radiator: 'Radiator', thermostat: 'Thermostat',
  clutch: 'Clutch', gearbox: 'Gearbox', transmission: 'Transmission', differential: 'Differential',
  suspension: 'Suspension', shock_absorber: 'Shock absorber', steering: 'Steering',
  electrical_wiring: 'Electrical wiring', lights: 'Lights',
  ac_compressor: 'AC compressor', ac_recharge: 'AC recharge',
  engine_overhaul: 'Engine overhaul', engine_repair: 'Engine repair', turbo: 'Turbo',
  bodywork: 'Bodywork', paint: 'Paint', glass: 'Glass',
  periodic_inspection: 'Periodic inspection', general_service: 'General service',
  basic_repair: 'Basic repair',
  breakdown_recovery: 'Breakdown recovery', accident_repair: 'Accident repair',
  other: 'Other',
};
const typeLabel = (t: string) => TYPE_LABEL[t] ?? t;

/**
 * The eight stages, in the order the server enforces them, with the sentence
 * that says what has to happen next. The wording is deliberately an
 * instruction and not a status: "Needs a diagnosis" tells the next person what
 * to do, where "reported" only tells them where it sits.
 */
const PIPELINE: Array<{ status: string; label: string; next: string }> = [
  { status: 'reported',              label: 'Reported',    next: 'Needs a diagnosis' },
  { status: 'diagnosed',             label: 'Diagnosed',   next: 'Ready to start' },
  { status: 'in_progress',           label: 'In the shop', next: 'Finish it and photograph it' },
  { status: 'work_complete',         label: 'Work done',   next: 'Attach the invoice, send to check' },
  { status: 'pending_verification',  label: 'To check',    next: 'Someone else must confirm it' },
  { status: 'verified',              label: 'Checked',     next: 'Waiting on approval' },
  { status: 'approved_for_payment',  label: 'Approved',    next: 'Ready to pay' },
  { status: 'paid',                  label: 'Paid',        next: '—' },
];

const NEXT_STEP: Record<string, string> = Object.fromEntries(PIPELINE.map((p) => [p.status, p.next]));

const CLOSED = ['paid', 'cancelled'];
const isOpenStatus = (s: string) => !CLOSED.includes(s);

/**
 * Tyre work is recorded rather than inspected, so it never walks the ladder and
 * its instruction is the same at every open stage: the reading and the picture.
 * Kept in step with TYRE_EVENT_TYPES on the server, which is what enforces it.
 */
const TYRE_TYPES = ['tire_replacement', 'tire_rotation', 'tire_puncture', 'tire_wear'];
const nextStep = (e: { status: string; event_type: string }) =>
  !isOpenStatus(e.status) ? '—'
    : TYRE_TYPES.includes(e.event_type) ? 'Odometer + tyre photo, then close'
      : (NEXT_STEP[e.status] ?? '—');

export default function MaintenancePage() {
  const { user } = useAuth();
  const [openEvent, setOpenEvent] = useState<string | null>(null);
  /** Due by date, or due by distance. Different trucks are governed by each. */
  const [dueSort, setDueSort] = useState<'date' | 'odometer'>('date');
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  // Setting a plan is an edit, and the server checks the permission anyway;
  // this only decides whether the controls are worth drawing.
  const canPlan = isAdmin || user?.role === 'fleet_manager' || user?.role === 'fleet_officer';

  const [tab, setTab] = useState<Tab>('events');
  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [byDriver, setByDriver] = useState<ByDriverRow[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingRow[]>([]);
  const [parts, setParts] = useState<PartRow[]>([]);
  const [cost, setCost] = useState<CostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DashSummary>({});

  // Entry sheets + the vehicle-history drawer.
  const [quickOpen, setQuickOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [history, setHistory] = useState<{ id: string; plate: string | null } | null>(null);

  // Toolbar state. `stage` is the pipeline filter: 'all', 'open', or one of
  // the eight statuses.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<string>('open');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortBy>('date_desc');

  useEffect(() => {
    const tm = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const refreshSummary = useCallback(async () => {
    try {
      const r = await apiClient.get<DashSummary>('/api/v1/maintenance/dashboard-summary');
      setSummary(r.data ?? {});
    } catch { /* silent — permission-gated */ }
  }, []);
  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'events') {
        const [ev, bd] = await Promise.all([
          apiClient.get<MaintenanceEvent[]>('/api/v1/maintenance/events?limit=500'),
          apiClient.get<ByDriverRow[]>('/api/v1/maintenance/by-driver').catch(() => ({ data: [] as ByDriverRow[] })),
        ]);
        setEvents(ev.data);
        setByDriver(bd.data ?? []);
      } else if (tab === 'upcoming') {
        const r = await apiClient.get<UpcomingRow[]>('/api/v1/maintenance/schedule/upcoming?days=30');
        setUpcoming(r.data);
      } else if (tab === 'parts') {
        const r = await apiClient.get<PartRow[]>('/api/v1/maintenance/parts');
        setParts(r.data);
      } else if (tab === 'cost') {
        const r = await apiClient.get<CostRow[]>('/api/v1/maintenance/reports/cost-per-km');
        setCost(r.data);
      }
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const afterSave = () => { setQuickOpen(false); setProOpen(false); refresh(); refreshSummary(); };

  // Types present in the data, for the filter dropdown.
  const typeOptions = useMemo(() => {
    const set = new Set(events.map((e) => e.event_type));
    return Array.from(set).sort((a, b) => typeLabel(a).localeCompare(typeLabel(b)));
  }, [events]);

  /** How many jobs stand at each stage. The strip is built from this. */
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) counts[e.status] = (counts[e.status] ?? 0) + 1;
    return counts;
  }, [events]);

  const openCount = useMemo(() => events.filter((e) => isOpenStatus(e.status)).length, [events]);

  const filtered = useMemo(() => {
    let list = events.slice();
    if (stage === 'open') list = list.filter((e) => isOpenStatus(e.status));
    else if (stage !== 'all') list = list.filter((e) => e.status === stage);
    if (typeFilter !== 'all') list = list.filter((e) => e.event_type === typeFilter);
    if (search) {
      list = list.filter((e) =>
        (e.vehicle_plate ?? '').toLowerCase().includes(search) ||
        (e.driver_name ?? '').toLowerCase().includes(search) ||
        typeLabel(e.event_type).toLowerCase().includes(search) ||
        (e.description ?? '').toLowerCase().includes(search),
      );
    }
    const cmp: Record<SortBy, (a: MaintenanceEvent, b: MaintenanceEvent) => number> = {
      date_desc: (a, b) => (b.reported_at ?? '').localeCompare(a.reported_at ?? ''),
      date_asc: (a, b) => (a.reported_at ?? '').localeCompare(b.reported_at ?? ''),
      cost_desc: (a, b) => Number(b.total_cost_sar ?? 0) - Number(a.total_cost_sar ?? 0),
      vehicle: (a, b) => (a.vehicle_plate ?? '').localeCompare(b.vehicle_plate ?? ''),
      driver: (a, b) => (a.driver_name ?? '').localeCompare(b.driver_name ?? ''),
    };
    return list.sort(cmp[sortBy]);
  }, [events, stage, typeFilter, search, sortBy]);

  const hasFilters = !!search || stage !== 'open' || typeFilter !== 'all';

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> Maintenance</div>
          {isAdmin && (
            <Button size="sm" className="gap-2" onClick={() => setQuickOpen(true)} data-log-button>
              <Plus className="h-4 w-4" /> Log
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        {/* PR-MAY17 tile, trimmed. Three meaningful tiles only. */}
        <div className="grid grid-cols-3 gap-2" data-control-tiles>
          <Tile label="Open jobs" value={summary.open_events ?? openCount}
                onClick={() => { setTab('events'); setStage('open'); }} />
          <Tile label="Oil due soon" value={summary.oil_due_soon ?? 0}
                accent={(summary.oil_due_soon ?? 0) > 0 ? 'amber' : undefined}
                onClick={() => { setTab('events'); setStage('all'); setTypeFilter('oil_change'); }} />
          <Tile label="This month" value={fmtSar0(Number(summary.month_spend_sar ?? 0))} />
        </div>

        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'events',   l: 'Jobs',        i: ClipboardList },
            { v: 'upcoming', l: 'Due soon',    i: AlertTriangle },
            { v: 'plan',     l: 'Service plan', i: CalendarClock },
            { v: 'inspections', l: 'Inspections', i: ClipboardCheck },
            { v: 'vehicles',  l: 'Vehicles',    i: Truck },
            { v: 'parts',    l: 'Spare parts', i: Box },
            { v: 'cost',     l: 'Worst by SAR/km', i: Wrench },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-4 py-2 text-sm capitalize border-b-2 transition-colors -mb-px inline-flex items-center gap-1.5 whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              <x.i className="h-3.5 w-3.5" /> {x.l}
            </button>
          ))}
        </div>

        {/* JOBS */}
        {tab === 'events' && (
          <>
            {/* The process itself, with the work standing at each stage. This
                is the part that was missing: eight stages exist and are
                enforced, and until now none of them were on screen. */}
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">How a job moves</div>
                <button type="button" onClick={() => setStage(stage === 'all' ? 'open' : 'all')}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                  {stage === 'all' ? 'Show open only' : 'Show everything'}
                </button>
              </div>
              <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
                {PIPELINE.map((p, i) => {
                  const n = stageCounts[p.status] ?? 0;
                  const active = stage === p.status;
                  return (
                    <div key={p.status} className="flex items-stretch shrink-0">
                      <button type="button"
                        onClick={() => setStage(active ? 'open' : p.status)}
                        title={p.next}
                        className={'rounded-md border px-2.5 py-1.5 text-start min-w-[6.5rem] transition-colors ' + (active
                          ? 'border-foreground bg-muted/60'
                          : n > 0
                            ? 'border-border hover:border-foreground/40 bg-background'
                            : 'border-border/60 bg-background text-muted-foreground/60')}>
                        <div className="text-[10px] uppercase tracking-wider truncate">{p.label}</div>
                        <div className={'text-lg font-semibold tabular-nums leading-tight ' + (n > 0 ? '' : 'opacity-40')}>{n}</div>
                      </button>
                      {i < PIPELINE.length - 1 && (
                        <ChevronRight className="h-3.5 w-3.5 self-center mx-0.5 text-muted-foreground/40 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
              {(stageCounts.cancelled ?? 0) > 0 && (
                <button type="button" onClick={() => setStage(stage === 'cancelled' ? 'open' : 'cancelled')}
                  className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2">
                  {stageCounts.cancelled} cancelled
                </button>
              )}
            </div>

            {byDriver.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" /> Most maintenance by driver
                </div>
                <div className="flex flex-wrap gap-2">
                  {byDriver.slice(0, 6).map((d) => (
                    <button key={d.driver_id} type="button"
                      onClick={() => setSearchInput(d.driver_name ?? '')}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs hover:border-primary/60 transition-colors">
                      <span className="font-medium">{d.driver_name ?? 'Unknown'}</span>
                      <span className="text-muted-foreground">{d.event_count} job{d.event_count === 1 ? '' : 's'}</span>
                      {Number(d.total_cost_sar ?? 0) > 0 && <span className="text-muted-foreground">· {fmtSar0(Number(d.total_cost_sar))}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search plate, driver, or type…" className="ps-10" data-maint-search />
                </div>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-11 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="all">All types</option>
                  {typeOptions.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)}
                  className="h-11 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="date_desc">Newest first</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="cost_desc">Highest cost</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="driver">Driver</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loading ? (
                <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : error ? (
                <div className="py-16 text-center text-sm text-rose-600 dark:text-rose-400">{error}</div>
              ) : filtered.length === 0 ? (
                <div className="py-16 text-center">
                  <Wrench className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                  <p className="text-sm text-muted-foreground">
                    {stage !== 'all' && stage !== 'open' && !search && typeFilter === 'all'
                      ? `Nothing is standing at “${PIPELINE.find((p) => p.status === stage)?.label ?? stage}”.`
                      : hasFilters ? 'No maintenance matches your filters.' : 'No maintenance recorded yet.'}
                  </p>
                  {!hasFilters && isAdmin && (
                    <Button size="sm" className="mt-4 gap-2" onClick={() => setQuickOpen(true)}><Plus className="h-4 w-4" /> Log the first one</Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          {['Date', 'Vehicle', 'Driver', 'Type', 'Cost', 'Photo', 'Stage', 'Next step'].map((h) => (
                            <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((e) => (
                          <tr key={e.id} onClick={() => setOpenEvent(e.id as string)}
                            className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer">
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{(e.reported_at ?? '').slice(0, 10) || '—'}</td>
                            <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}>
                              {e.vehicle_id ? (
                                <button type="button"
                                  onClick={() => setHistory({ id: e.vehicle_id as string, plate: e.vehicle_plate })}
                                  className="inline-flex items-center gap-1.5 font-mono text-xs hover:underline" dir="ltr" data-vehicle-link>
                                  <Truck className="h-3 w-3 text-muted-foreground" /> {e.vehicle_plate ?? '—'}
                                </button>
                              ) : (
                                <span className="font-mono text-xs text-muted-foreground" dir="ltr">{e.vehicle_plate ?? '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">{e.driver_name ?? <span className="text-muted-foreground italic">—</span>}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5">
                                {typeLabel(e.event_type)}
                                {e.is_repeat_issue && (
                                  <span className="rounded-full bg-rose-500/10 border border-rose-500/40 text-rose-700 dark:text-rose-400 px-1.5 py-0.5 text-[10px]"
                                    title={e.days_since_prior ? `Same issue ${e.days_since_prior}d ago` : 'Repeat issue'}>↻</span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono tabular-nums whitespace-nowrap">{Number(e.total_cost_sar ?? 0) > 0 ? fmtSar(Number(e.total_cost_sar)) : '—'}</td>
                            <td className="px-4 py-3">
                              {(e.photo_count ?? 0) > 0
                                ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Camera className="h-3.5 w-3.5" />{e.photo_count}</span>
                                : <span className="text-muted-foreground"><Camera className="h-3.5 w-3.5 opacity-30" /></span>}
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                            {/* The instruction, not the status. */}
                            <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                              {nextStep(e)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground bg-muted/20">
                    Showing {filtered.length} of {events.length}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* DUE SOON */}
        {tab === 'upcoming' && (
          loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
          ) : upcoming.length === 0 ? (
            /* Nothing due is an achievement only if something was ever
               scheduled. With no plan at all it is silence, not safety — so
               the empty state now says which of the two this is. */
            <div className="rounded-lg border border-border p-10 text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="mt-3 font-medium">Nothing due in the next 30 days</div>
              <p className="text-sm text-muted-foreground mt-1">
                Every truck with a service plan is inside its window, by date and by distance.
              </p>
              <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={() => setTab('plan')}>
                <CalendarClock className="h-3.5 w-3.5" /> Check the service plan
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Sorted by whichever clock governs: a truck on a date schedule
                  and one on a distance schedule are not comparable, so the
                  order says which question is being asked. */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Sort by</span>
                {(['date', 'odometer'] as const).map((k) => (
                  <button key={k} onClick={() => setDueSort(k)}
                    className={'px-2.5 py-1 rounded-full border text-xs capitalize transition ' + (dueSort === k
                      ? 'border-foreground font-medium'
                      : 'border-border text-muted-foreground hover:text-foreground')}>
                    {k}
                  </button>
                ))}
                <span className="text-xs text-muted-foreground ms-auto">{upcoming.length} due</span>
              </div>

              {[...upcoming].sort((a, b) => {
                const big = Number.MAX_SAFE_INTEGER;
                if (dueSort === 'odometer') return (a.km_remaining ?? big) - (b.km_remaining ?? big);
                return (a.days_remaining ?? big) - (b.days_remaining ?? big);
              }).map((u) => {
                const urgent = (u.days_remaining !== null && u.days_remaining <= 3) || (u.km_remaining !== null && u.km_remaining <= 200);
                return (
                  <div key={u.id} className={'rounded-lg border p-3 ' + (urgent
                    ? 'border-rose-500 bg-rose-500/10'
                    : 'border-amber-500/30')}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {urgent && <AlertTriangle className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 shrink-0" />}
                        {typeLabel(u.event_type)}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono" dir="ltr">{u.vehicle_plate ?? '—'}</div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {u.days_remaining !== null && (
                        <span className={'text-[11px] rounded-full border px-2 py-0.5 ' + (u.days_remaining <= 3
                          ? 'border-rose-500 text-rose-600 dark:text-rose-400 font-medium'
                          : 'border-border text-muted-foreground')}>
                          {u.days_remaining <= 0 ? 'Overdue' : `Due in ${u.days_remaining} days`}
                        </span>
                      )}
                      {u.km_remaining !== null && u.current_odometer_km !== null && (
                        <span className={'text-[11px] rounded-full border px-2 py-0.5 ' + (u.km_remaining <= 200
                          ? 'border-rose-500 text-rose-600 dark:text-rose-400 font-medium'
                          : 'border-border text-muted-foreground')}>
                          {u.km_remaining <= 0
                            ? 'Distance passed'
                            : `${Number(u.km_remaining).toLocaleString('en-US')} km remaining`}
                          {' '}(current {Number(u.current_odometer_km).toLocaleString('en-US')} km)
                        </span>
                      )}
                      {(u.next_due_date ?? u.next_service_due_date) && (
                        <span className="text-[11px] rounded-full border border-border px-2 py-0.5 text-muted-foreground font-mono">
                          {u.next_due_date ?? u.next_service_due_date}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === 'plan' && <ServicePlanPanel typeLabel={typeLabel} canEdit={!!canPlan} />}

        {tab === 'inspections' && <InspectionsPanel />}

        {tab === 'vehicles' && <VehiclesPanel />}

        {tab === 'parts' && <SparePartsPanel typeLabel={typeLabel} />}

        {tab === 'cost' && (
          loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
          ) : cost.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No cost data yet.</div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                    <tr>
                      <th className="text-start px-3 py-2">Vehicle</th>
                      <th className="text-end px-3 py-2">Maint cost (paid)</th>
                      <th className="text-end px-3 py-2">Odometer km</th>
                      <th className="text-end px-3 py-2">SAR / km</th>
                      <th className="text-start px-3 py-2">Standing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cost.map((c, i) => {
                      /*
                       * An outlier is not simply an expensive truck. A truck
                       * that costs a lot and covers the distance is earning its
                       * keep; one that costs a lot while barely moving is the
                       * one to sell. Flagged only when both are true: cost per
                       * km above three times the fleet median, and mileage in
                       * the bottom quarter.
                       */
                      const rates = cost.map((x) => Number(x.sar_per_km)).filter((x) => x > 0).sort((a, b) => a - b);
                      const median = rates.length ? rates[Math.floor(rates.length / 2)] : 0;
                      const kms = cost.map((x) => Number(x.current_odometer_km ?? 0)).filter((x) => x > 0).sort((a, b) => a - b);
                      const lowKm = kms.length ? kms[Math.floor(kms.length * 0.25)] : 0;
                      const rate = Number(c.sar_per_km ?? 0);
                      const km = Number(c.current_odometer_km ?? 0);
                      const liquidate = median > 0 && rate > median * 3 && km > 0 && km <= lowKm;
                      const heavy = !liquidate && median > 0 && rate > median * 2;
                      return (
                      <tr key={(c.plate_en ?? c.vehicle_code ?? '') + i}
                        className={'border-b border-border last:border-b-0 hover:bg-muted/20 ' + (liquidate ? 'bg-rose-500/5' : '')}>
                        <td className="px-3 py-2 font-mono" dir="ltr">{c.plate_en ?? c.vehicle_code ?? '—'}</td>
                        <td className="px-3 py-2 text-end font-mono">{fmtSar(Number(c.maintenance_cost_sar))}</td>
                        <td className="px-3 py-2 text-end font-mono">{c.current_odometer_km ? Number(c.current_odometer_km).toLocaleString('en-US') : '—'}</td>
                        <td className={'px-3 py-2 text-end font-mono ' + (liquidate ? 'text-rose-600 dark:text-rose-400 font-medium' : '')}>
                          {c.sar_per_km !== null ? Number(c.sar_per_km).toFixed(3) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {liquidate ? (
                            <span className="text-[11px] rounded-full border border-rose-500 text-rose-600 dark:text-rose-400 px-2 py-0.5 whitespace-nowrap">
                              Sell — high cost, low distance
                            </span>
                          ) : heavy ? (
                            <span className="text-[11px] rounded-full border border-amber-500/50 text-amber-600 dark:text-amber-400 px-2 py-0.5 whitespace-nowrap">
                              Costly, but earning it
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground px-3 py-2 border-t border-border">
                Cost per kilometre uses <b>paid</b> maintenance against the odometer. A truck is flagged to sell
                only when it is both far above the fleet’s median rate and in the bottom quarter for distance —
                an expensive truck that does the work is not a liability.
              </p>
            </div>
          )
        )}
      </main>

      {quickOpen && (
        <MaintenanceQuickLogSheet
          onClose={() => setQuickOpen(false)}
          onSaved={afterSave}
          onAdvanced={() => { setQuickOpen(false); setProOpen(true); }}
        />
      )}
      {proOpen && <MaintenanceProSheet onClose={() => setProOpen(false)} onSaved={afterSave} />}
      {openEvent && (
        <JobStepper
          eventId={openEvent}
          typeLabel={typeLabel}
          onClose={() => setOpenEvent(null)}
          onChanged={() => { refresh(); refreshSummary(); }}
        />
      )}
      {history && (
        <VehicleHistorySheet vehicleId={history.id} plate={history.plate} typeLabel={typeLabel} onClose={() => setHistory(null)} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'paid'
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
    : status === 'cancelled'
      ? 'bg-muted text-muted-foreground border-border'
      : status === 'rejected'
        ? 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30'
        : 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
  const label = PIPELINE.find((p) => p.status === status)?.label ?? status.replace(/_/g, ' ');
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] capitalize whitespace-nowrap ${tone}`}>{label}</span>;
}

// PR-MAY17 tile, trimmed. onClick focuses a filter; accent draws the eye.
function Tile({ label, value, accent, onClick }: {
  label: string; value: number | string; accent?: 'amber' | 'rose'; onClick?: () => void;
}) {
  const tone = accent === 'rose'
    ? 'border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-300'
    : accent === 'amber'
      ? 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300'
      : 'border-border bg-card';
  const body = (
    <div className={`rounded-lg border p-3 ${tone}`} data-control-tile={label}>
      <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-lg font-semibold tabular-nums leading-tight mt-0.5">{value}</div>
    </div>
  );
  if (onClick) return <button type="button" onClick={onClick} className="block text-start w-full">{body}</button>;
  return body;
}
